import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  ArrowLeft,
  Save,
  Upload,
  Image as ImageIcon,
  Type,
  FileText,
  Check,
  Loader2,
  ChevronDown,
  ChevronUp,
  Home,
  Palette,
  Lightbulb,
  Cloud,
  Users,
  Phone,
  Settings,
} from "lucide-react";
import { toast } from "sonner";

const PAGE_CONFIG = [
  { id: "home", label: "Accueil", icon: Home },
  { id: "dashboard", label: "Dashboard", icon: Settings },
  { id: "design-durable", label: "Design Durable", icon: Palette },
  { id: "innovation", label: "Innovation", icon: Lightbulb },
  { id: "climat", label: "Strategie Climat", icon: Cloud },
  { id: "rse", label: "Strategie RSE", icon: Users },
  { id: "contact", label: "Contact", icon: Phone },
];

const ImageUploader = ({ value, onChange, token }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      onChange(res.data.url);
      toast.success("Image uploadee");
    } catch (err) {
      toast.error("Erreur upload: " + (err.response?.data?.detail || "Erreur"));
    }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      {value && (
        <div className="w-32 h-32 rounded-xl overflow-hidden border border-[var(--sand)] bg-[var(--sand)]">
          <img src={value} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input-premium text-sm flex-1 pl-3"
          placeholder="URL de l'image"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-[var(--sand)] px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-[var(--sand-dark)] transition-colors flex-shrink-0"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          Upload
        </button>
      </div>
    </div>
  );
};

const FieldEditor = ({ field, onChange, token }) => {
  if (field.type === "image") {
    return (
      <div>
        <label className="text-sm font-medium text-[var(--charcoal)] mb-2 block">
          {field.label}
        </label>
        <ImageUploader value={field.value} onChange={onChange} token={token} />
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div>
        <label className="text-sm font-medium text-[var(--charcoal)] mb-2 block">
          {field.label}
        </label>
        <textarea
          value={field.value}
          onChange={(e) => onChange(e.target.value)}
          className="input-premium text-sm min-h-[80px] resize-y pl-3"
          rows={3}
        />
      </div>
    );
  }

  return (
    <div>
      <label className="text-sm font-medium text-[var(--charcoal)] mb-2 block">
        {field.label}
      </label>
      <input
        type="text"
        value={field.value}
        onChange={(e) => onChange(e.target.value)}
        className="input-premium text-sm pl-3"
      />
    </div>
  );
};

const SectionEditor = ({ section, onChange, token }) => {
  const [open, setOpen] = useState(true);

  const handleFieldChange = (fieldIdx, newValue) => {
    const updated = { ...section };
    updated.fields = [...updated.fields];
    updated.fields[fieldIdx] = { ...updated.fields[fieldIdx], value: newValue };
    onChange(updated);
  };

  return (
    <div className="card-premium overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <h3 className="font-display text-lg font-bold text-[var(--charcoal)]">
          {section.label}
        </h3>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[var(--stone)]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[var(--stone)]" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-[var(--sand)]">
          <div className="pt-4" />
          {section.fields.map((field, idx) => (
            <FieldEditor
              key={field.key}
              field={field}
              onChange={(val) => handleFieldChange(idx, val)}
              token={token}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminContent() {
  const { token } = useAuth();
  const [activePage, setActivePage] = useState("home");
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const loadContent = async (pageId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/content/${pageId}`, { headers });
      setContent(res.data);
      setHasChanges(false);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Acces reserve aux administrateurs");
      } else {
        toast.error("Erreur de chargement");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    loadContent(activePage);
  }, [activePage]);

  const handleSectionChange = (sectionIdx, updatedSection) => {
    const updated = { ...content };
    updated.sections = [...updated.sections];
    updated.sections[sectionIdx] = updatedSection;
    setContent(updated);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/content/${activePage}`,
        { sections: content.sections },
        { headers }
      );
      toast.success("Contenu sauvegarde !");
      setHasChanges(false);
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
    setSaving(false);
  };

  const addSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      label: "Nouvelle section",
      fields: [
        { key: `field_${Date.now()}`, label: "Nouveau champ", type: "text", value: "" },
      ],
    };
    const updated = { ...content };
    updated.sections = [...(updated.sections || []), newSection];
    setContent(updated);
    setHasChanges(true);
  };

  const addFieldToSection = (sectionIdx, type = "text") => {
    const updated = { ...content };
    updated.sections = [...updated.sections];
    const section = { ...updated.sections[sectionIdx] };
    section.fields = [
      ...section.fields,
      {
        key: `field_${Date.now()}`,
        label: type === "image" ? "Nouvelle image" : type === "textarea" ? "Nouveau texte long" : "Nouveau champ",
        type,
        value: "",
      },
    ];
    updated.sections[sectionIdx] = section;
    setContent(updated);
    setHasChanges(true);
  };

  const removeField = (sectionIdx, fieldIdx) => {
    const updated = { ...content };
    updated.sections = [...updated.sections];
    const section = { ...updated.sections[sectionIdx] };
    section.fields = section.fields.filter((_, i) => i !== fieldIdx);
    updated.sections[sectionIdx] = section;
    setContent(updated);
    setHasChanges(true);
  };

  const removeSection = (sectionIdx) => {
    if (!window.confirm("Supprimer cette section ?")) return;
    const updated = { ...content };
    updated.sections = updated.sections.filter((_, i) => i !== sectionIdx);
    setContent(updated);
    setHasChanges(true);
  };

  return (
    <div className="min-h-screen bg-[var(--bone)] pt-28 pb-16" data-testid="admin-content-page">
      <div className="container-premium">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-[var(--stone)] hover:text-[var(--terracotta)] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-[var(--charcoal)]">
              Gestion du contenu
            </h1>
            <p className="text-[var(--stone)] mt-1">
              Modifiez les textes, images et liens de chaque page
            </p>
          </div>
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
              data-testid="save-content-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              Sauvegarder
            </button>
          )}
        </div>

        {/* Page tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-8 scrollbar-hide">
          {PAGE_CONFIG.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActivePage(id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                activePage === id
                  ? "bg-[var(--terracotta)] text-white shadow-lg"
                  : "bg-white text-[var(--stone)] hover:bg-[var(--sand)]"
              }`}
              data-testid={`page-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content editor */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[var(--terracotta)] animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {(content?.sections || []).map((section, sIdx) => (
              <div key={section.id}>
                <SectionEditor
                  section={section}
                  onChange={(updated) => handleSectionChange(sIdx, updated)}
                  token={token}
                />
                {/* Add field buttons */}
                <div className="flex items-center gap-2 mt-2 ml-5">
                  <button
                    onClick={() => addFieldToSection(sIdx, "text")}
                    className="text-xs text-[var(--stone)] hover:text-[var(--terracotta)] flex items-center gap-1"
                  >
                    <Type className="w-3 h-3" /> + Texte
                  </button>
                  <button
                    onClick={() => addFieldToSection(sIdx, "textarea")}
                    className="text-xs text-[var(--stone)] hover:text-[var(--terracotta)] flex items-center gap-1"
                  >
                    <FileText className="w-3 h-3" /> + Paragraphe
                  </button>
                  <button
                    onClick={() => addFieldToSection(sIdx, "image")}
                    className="text-xs text-[var(--stone)] hover:text-[var(--terracotta)] flex items-center gap-1"
                  >
                    <ImageIcon className="w-3 h-3" /> + Image
                  </button>
                  <div className="flex-1" />
                  <button
                    onClick={() => removeSection(sIdx)}
                    className="text-xs text-red-400 hover:text-red-600"
                  >
                    Supprimer la section
                  </button>
                </div>
              </div>
            ))}

            {/* Add section */}
            <button
              onClick={addSection}
              className="w-full py-4 border-2 border-dashed border-[var(--sand)] rounded-2xl text-[var(--stone)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)] transition-colors flex items-center justify-center gap-2"
              data-testid="add-section-btn"
            >
              + Ajouter une section
            </button>

            {/* Floating save button */}
            {hasChanges && (
              <div className="fixed bottom-6 right-6 z-50">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[var(--terracotta)] text-white px-6 py-3 rounded-2xl shadow-xl flex items-center gap-2 font-medium hover:bg-[var(--terracotta-dark)] transition-colors"
                  data-testid="floating-save-btn"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Check className="w-5 h-5" />
                  )}
                  Sauvegarder les modifications
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
